import { useState } from 'react';
import { Box, Button, ButtonProps, TextField } from '@mui/material';

export interface InlineAddFieldProps {
  // The button's text, and the name of the field it opens.
  label: string;
  maxLength: number;
  /** Gets the trimmed entry, never blank; a refusal it returns keeps the entry open and shows under it. */
  onAdd: (text: string) => string | null | Promise<string | null>;
  buttonProps?: ButtonProps;
}

/** A "+ Add" button that opens a one-line field, added with Enter or Add and closed with Escape or Cancel. */
export const InlineAddField = ({ label, maxLength, onAdd, buttonProps }: InlineAddFieldProps) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const finish = (refused: string | null) => {
    setRefusal(refused);
    if (refused) return;
    setDraft('');
    setAdding(false);
  };

  // An answer that doesn't wait closes the field in the same render, as a synchronous add expects.
  const add = async () => {
    const typed = draft.trim();
    if (!typed || saving) return;
    const result = onAdd(typed);
    if (!(result instanceof Promise)) return finish(result);
    setSaving(true);
    const refused = await result;
    setSaving(false);
    finish(refused);
  };

  const cancel = () => {
    setRefusal(null);
    setAdding(false);
  };

  if (!adding) {
    return (
      <Button
        size="small"
        variant="outlined"
        {...buttonProps}
        onClick={() => setAdding(true)}>
        {label}
      </Button>
    );
  }

  return (
    <Box className="flex items-center gap-2 mt-1">
      <TextField
        size="small"
        // Mounts only after the button is pressed, so the focus follows the user's own action.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={draft}
        disabled={saving}
        error={Boolean(refusal)}
        helperText={refusal}
        inputProps={{ 'aria-label': label, maxLength }}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') add();
          if (event.key === 'Escape') cancel();
        }}
      />
      <Button
        size="small"
        variant="contained"
        disabled={saving}
        onClick={add}>
        Add
      </Button>
      <Button
        size="small"
        disabled={saving}
        onClick={cancel}>
        Cancel
      </Button>
    </Box>
  );
};
